/**
 * Module Federation runtime plugin — make the host's share-scope entries
 * consumable by the webpack-built (classic MF1) remotes.
 *
 * Root cause:
 *   The host (rspack MF2, named "signals") registers react in the share scope
 *   as `{ loaded: true }` — no `eager` field — because rspack uses a different
 *   sharing mechanism than webpack MF1. The actual signals remote (also named
 *   "signals") cannot overwrite the host's entry because the `register()`
 *   condition `uniqueName > activeVersion.from` evaluates "signals" > "signals"
 *   = false. So the scope entry stays without `eager`.
 *
 *   When agent-builder's vendor chunks load on navigation to /agent-builder,
 *   `loadSingletonVersion("default", "react", true, ...)` calls `eagerOnly()`
 *   which filters to only entries with `eager` truthy. Finding none, it returns
 *   `{}`, `findSingletonVersionKey` returns `0`, and `get(undefined)` crashes
 *   with "Cannot set properties of undefined (setting 'loaded')".
 *
 * Fix:
 *   `beforeRequest` fires on EVERY `loadRemote()` call, including navigations
 *   when `this.inited` is already true (so `beforeInitContainer` is skipped).
 *   We sync the host's scope to `globalThis.__webpack_share_scopes__` AND patch
 *   `eager: true` on every entry before `module.get()` loads the vendor chunks.
 *
 *   The scope sync is also needed at script-eval time: MF1 remotes alias
 *   `__webpack_require__.S` to `globalThis.__webpack_share_scopes__` when their
 *   bundle evaluates, before `init()` is called.
 *
 * IMPORTANT: this module must NOT statically import shared packages (react,
 * react-dom, …). It is loaded *during* federation init, before the shared
 * consume factories exist — importing a shared module here triggers a consume
 * with no factory yet and crashes with "Cannot read properties of undefined
 * (reading 'call')". Keep this plugin import-free.
 */

function patchEager(scope) {
  for (const versions of Object.values(scope || {})) {
    for (const entry of Object.values(versions || {})) {
      if (entry && !entry.eager) {
        entry.eager = true;
      }
    }
  }
}

// Singletons whose DOM ownership makes a second live copy catastrophic:
// two react-dom instances reconciling the same tree produce the
// `removeChild/insertBefore ... not a child of this node` commit loops seen in
// the 2026-07 incident (rspack cutover).
const DOM_SINGLETONS = ['react', 'react-dom'];

const plugin = () => ({
  name: 'eager-shared-plugin',

  /**
   * Belt-and-suspenders for `shareStrategy: 'loaded-first'` (config.js): when
   * the runtime resolves one of the DOM singletons, prefer a share-scope entry
   * that is already loaded/loading over the default resolver's version-ordered
   * pick. An already-live copy is by definition the one rendering the page —
   * returning it can never introduce a second React. Hook contract verified
   * against @module-federation/runtime-core 0.20.0 `getRegisteredShare`:
   * args = { shareScopeMap, scope, pkgName, version, GlobalFederation,
   * resolver }; the returned args' `resolver()` supplies the entry.
   */
  resolveShare(args) {
    if (!DOM_SINGLETONS.includes(args.pkgName)) return args;
    const versions = args.shareScopeMap?.[args.scope]?.[args.pkgName];
    if (!versions) return args;

    // Prefer a fully-loaded entry strictly over a merely-loading one, in two
    // passes. A single combined pass is order-sensitive: JS enumerates
    // integer-like keys ('0' — the "version 0 advertisement" shape documented
    // above) BEFORE string keys ('18.3.1'), so a loading version-0 entry
    // would beat the host's already-loaded 18.3.1 and hand the slot back to
    // the wrong copy. Verified: Object.keys({'18.3.1':…, '0':…}) → ['0','18.3.1'].
    const loadedKey =
      Object.keys(versions).find((v) => versions[v]?.loaded) ??
      Object.keys(versions).find((v) => versions[v]?.loading);

    if (loadedKey && loadedKey !== args.version) {
      const defaultResolver = args.resolver;
      args.resolver = () => versions[loadedKey] ?? defaultResolver();
    }
    return args;
  },

  beforeRequest(args) {
    const { origin } = args;
    const shareScopeMap = origin?.shareScopeMap;
    if (!shareScopeMap) return args;

    // Sync scope references into the MF1 global so remote webpack runtimes
    // find the host's entries when their scripts evaluate.
    globalThis.__webpack_share_scopes__ = globalThis.__webpack_share_scopes__ || {};
    for (const [scopeName, scope] of Object.entries(shareScopeMap)) {
      if (!globalThis.__webpack_share_scopes__[scopeName]) {
        globalThis.__webpack_share_scopes__[scopeName] = scope;
      } else {
        Object.assign(globalThis.__webpack_share_scopes__[scopeName], scope);
      }
      // Patch eager: true on every entry in the host scope.
      // Must be here (not beforeInitContainer) — beforeInitContainer is skipped
      // when the remote is already inited, i.e. on every navigation after the
      // initial page load.
      patchEager(scope);
    }
    return args;
  },
});

export default plugin;
